import {
  actionCreator,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';
import type { ArdoqId } from '@ardoq/api-types';
import {
  clearDrafts,
  draftsForFutureState,
  finishAccept,
  startAccept,
  type AcceptFinishedPayload,
  type FutureStateDraftClaimsState,
  type FutureStateDrafts,
} from './futureStateDrafts';

type FutureStateIdPayload = { futureStateId: ArdoqId };

export type AcceptDraftsPayload = FutureStateIdPayload & {
  initiativeId: ArdoqId;
  drafts: FutureStateDrafts;
};

export type DraftsChangedPayload = {
  next: FutureStateDrafts;
  /** The latestDraftVersion that `next` was built from. */
  basedOnVersion: number;
};

export const futureStateDraftsChanged = actionCreator<DraftsChangedPayload>(
  '[futureStateDraftClaims] drafts changed'
);

export const futureStateDraftsCleared = actionCreator<FutureStateIdPayload>(
  '[futureStateDraftClaims] drafts cleared'
);

export const futureStateDraftsAccepted = actionCreator<AcceptDraftsPayload>(
  '[futureStateDraftClaims] drafts accepted'
);

export const futureStateDraftsAcceptFinished =
  actionCreator<AcceptFinishedPayload>(
    '[futureStateDraftClaims] drafts accept finished'
  );

const updateDrafts = (
  state: FutureStateDraftClaimsState,
  futureStateId: ArdoqId,
  update: (drafts: FutureStateDrafts) => FutureStateDrafts
): FutureStateDraftClaimsState =>
  state[futureStateId]
    ? { ...state, [futureStateId]: update(state[futureStateId]) }
    : state;

const clearFutureStateDrafts = (
  state: FutureStateDraftClaimsState,
  { futureStateId }: FutureStateIdPayload
) => updateDrafts(state, futureStateId, clearDrafts);

const acceptFutureStateDrafts = (
  state: FutureStateDraftClaimsState,
  { futureStateId }: AcceptDraftsPayload
) => updateDrafts(state, futureStateId, startAccept);

const finishFutureStateAccept = (
  state: FutureStateDraftClaimsState,
  payload: AcceptFinishedPayload
) =>
  updateDrafts(state, payload.futureStateId, drafts =>
    finishAccept(drafts, payload)
  );

/**
 * Ignores `next` when the drafts changed after it was built (a clear, an accept
 * or another batch), so a late commit cannot bring back replaced drafts.
 */
const replaceDrafts = (
  state: FutureStateDraftClaimsState,
  { next, basedOnVersion }: DraftsChangedPayload
): FutureStateDraftClaimsState => {
  const current = draftsForFutureState(state, next.futureStateId);
  return current.accepting || current.latestDraftVersion !== basedOnVersion
    ? state
    : { ...state, [next.futureStateId]: next };
};

export const futureStateDraftClaims$ =
  persistentReducedStream<FutureStateDraftClaimsState>(
    'futureStateDraftClaims$',
    {},
    [
      reducer(futureStateDraftsChanged, replaceDrafts),
      reducer(futureStateDraftsCleared, clearFutureStateDrafts),
      reducer(futureStateDraftsAccepted, acceptFutureStateDrafts),
      reducer(futureStateDraftsAcceptFinished, finishFutureStateAccept),
    ]
  );
